import PermissionButton from 'components/buttons/PermissionButton.tsx';
import { useModal } from 'components/modals/useModal.ts';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useUpdateTimecardIncrementMutation } from 'store/api/tenants.ts';
import { TenantConfigurationDto, TimecardIncrementUpdateDto } from 'types/tenants.ts';
import { errorAlert, successAlert } from 'utils/alert';
import { cn } from 'utils/cn.ts';
import { t } from 'utils/getTranslations.ts';
import { CONFIGURATION_CARD_CLASS, CONFIGURATION_CARD_HEADER_CLASS } from './configurationCard';
import TimecardIncrementsEditModal from './TimecardIncrementsEditModal.tsx';

interface Props {
    tenantConfiguration: TenantConfigurationDto;
}

const TimecardIncrementControl = ({ tenantConfiguration }: Props): JSX.Element => {
    const { openModal, closeModal } = useModal();
    const [updateTimecardIncrement] = useUpdateTimecardIncrementMutation();
    const { timecardIncrementValues, defaultTimecardIncrement } = tenantConfiguration;

    const onTimecardIncrementSubmit = useCallback(
        async (dto: TimecardIncrementUpdateDto): Promise<void> => {
            try {
                await updateTimecardIncrement(dto).unwrap();
                successAlert(t('configuration.timecard-increment-update-success'));
                closeModal();
            } catch (e) {
                console.error('onSubmit error: ', e);
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [closeModal, updateTimecardIncrement]
    );

    const openTimecardIncrementsEditModal = useCallback((): void => {
        openModal(
            <TimecardIncrementsEditModal
                initialData={{ timecardIncrementValues, defaultTimecardIncrement }}
                onSubmit={(dto) => {
                    void onTimecardIncrementSubmit(dto);
                }}
                onClose={closeModal}
            />,
            closeModal
        );
    }, [timecardIncrementValues, defaultTimecardIncrement, onTimecardIncrementSubmit, openModal, closeModal]);

    return (
        <>
            <h3 className="text-h3 text-balance">
                <FormattedMessage id="configuration.timecard-increment-control" />
            </h3>
            <div className={CONFIGURATION_CARD_CLASS}>
                <div className={CONFIGURATION_CARD_HEADER_CLASS}>
                    <FormattedMessage id="configuration.timecard-increment-values" />
                </div>
                <div className="p-4">
                    <div className="flex">
                        <div
                            className={cn(
                                'flex w-1/3 flex-col gap-2',
                                timecardIncrementValues?.length === 1 && 'justify-center'
                            )}
                        >
                            {timecardIncrementValues?.length ? (
                                timecardIncrementValues.map((value) => (
                                    <div key={value} className="flex items-center justify-between">
                                        <p className="text-body2">{value}</p>
                                        {value === defaultTimecardIncrement ? (
                                            <p className="text-body2 text-fg-secondary">
                                                <FormattedMessage id="configuration.timecard-increment-default" />
                                            </p>
                                        ) : null}
                                    </div>
                                ))
                            ) : (
                                <p className="text-body2">-</p>
                            )}
                        </div>
                        <div className="ml-10 flex items-center">
                            <PermissionButton
                                permissions={['UPDATE.BACK_OFFICE_SETTINGS']}
                                variant="outlined"
                                size="small"
                                className="h-8"
                                onClick={openTimecardIncrementsEditModal}
                            >
                                <FormattedMessage id="edit" />
                            </PermissionButton>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TimecardIncrementControl;
